import { logger } from 'lib/datadog/rum-logger'
import { CHECKBOX_DOM_ID } from './consts'
import type { Action } from './types'
import { assessUserCheckbox, assessUserInvisible, BOT_THRESHOLD } from './utils'

/* 
  2-TIERED RECAPTCHA ASSESSMENT
  -----------------------------
  1. User Clicks Submit (form fields already validated)
  2. Assess with invisible reCAPTCHA (v3), which outputs a token.
  3. Send token to POST /api/assess-recaptcha-token → returns a score.
     - If score > BOT_THRESHOLD → Proceed with submission. ✅ END
     - If score <= BOT_THRESHOLD → Require checkbox reCAPTCHA (v2).
  
  4. Checkbox reCAPTCHA outputs a new token.
  5. Send token to POST /api/assess-recaptcha-token → returns a score.
     - If score > BOT_THRESHOLD → Proceed with submission. ✅ END
     - If score <= BOT_THRESHOLD → return and go to form success callback. 🚨 END
       
  🔹 If a bot is caught at the checkbox stage, they must solve a puzzle to proceed. 
     - Only when solved does reCAPTCHA output a score.
     - Ideally, bots get stuck here, preventing fake submissions. however at this stage regardless of the score, once the puzzle is solved, we allow the submission to go through.

*/
const guardedSubmission = async (
	action: Action,
	formData: Record<string, unknown>,
	formId: string | number
) => {
	logger.info(`${action}_attempt`, { formId })

	// form data is not serializable, we need to convert it to a plain object for logging

	const invisibleTokenAsessmentRes = await assessUserInvisible({
		action,
		ctx: { formId, formData: formData },
	})

	// recaptcha check failed, treat as submission error (the submit handler will catch and handle this)
	const failedToAssess = 'error' in invisibleTokenAsessmentRes
	if (failedToAssess) {
		logger.error(`${action}_error`, {
			formId,
			error: invisibleTokenAsessmentRes.error,
			formattedFormData: formData,
		})
		throw new Error('Failed to assess user', {
			cause: invisibleTokenAsessmentRes.error,
		})
	}

	const isSuspectedBotTierOne =
		'score' in invisibleTokenAsessmentRes &&
		invisibleTokenAsessmentRes.score <= BOT_THRESHOLD

	if (isSuspectedBotTierOne) {
		logger.info(`${action}_bot`, {
			formId,
			formData: formData,
			...invisibleTokenAsessmentRes,
		})

		const checkboxTokenAssessmentRes = await assessUserCheckbox({
			action,
			ctx: { formId, formData: formData },
			containerId: CHECKBOX_DOM_ID,
		})

		// recaptcha check failed, treat as submission error (the submit handler will catch and handle this)
		const failedToAssess = 'error' in checkboxTokenAssessmentRes
		if (failedToAssess) {
			logger.error(`${action}_bot`, {
				formId,
				error: checkboxTokenAssessmentRes.error,
				formattedFormData: formData,
			})
			throw new Error('Failed to assess user', {
				cause: checkboxTokenAssessmentRes.error,
			})
		}
		const isSuspectedBotTierTwo =
			'score' in checkboxTokenAssessmentRes &&
			checkboxTokenAssessmentRes.score <= BOT_THRESHOLD

		if (isSuspectedBotTierTwo) {
			//puzzle was solved so despite low score, put it through
			logger.warn(`${action}_puzzle_solved`, {
				formId,
				score: checkboxTokenAssessmentRes.score,
				formattedFormData: formData,
			})
		}
	}
}

export default guardedSubmission
